import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Wallet, IndianRupee, Loader2, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

// Academic years — 2020-21 → 2030-31
function buildAcademicYears() {
  const years = [];
  for (let y = 2020; y <= 2030; y += 1) {
    const nx = String((y + 1) % 100).padStart(2, "0");
    years.push(`${y}-${nx}`);
  }
  return years;
}

// Current-financial-year style default. Between April and March the "current" AY is Y1-Y2.
function currentAcademicYear() {
  const now = new Date();
  const m = now.getMonth(); // 0-based
  const y = now.getFullYear();
  const startYear = m >= 3 ? y : y - 1; // Apr = 3
  const nx = String((startYear + 1) % 100).padStart(2, "0");
  return `${startYear}-${nx}`;
}

function formatINR(n) {
  const val = Number.isFinite(n) ? Number(n) : 0;
  return val.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseAmount(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const s = String(v).replace(/[,\s]/g, "").replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

const AY_LIST = buildAcademicYears();
const DEFAULT_AY = currentAcademicYear();

function FiltersCard({ ay, setAY, institutes, instId, setInstId, courseTypes, courseType, setCourseType, admissionProcesses, admProc, setAdmProc, categories, category, setCategory, isInstituteLevel, loadingMasters }) {
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="fee-builder-filters">
      <label className="block">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Academic Year</div>
        <select data-testid="fee-builder-ay" className={inputCls} value={ay} onChange={(e) => setAY(e.target.value)}>
          {AY_LIST.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Institute</div>
        <select data-testid="fee-builder-institute" className={inputCls} value={instId} onChange={(e) => setInstId(e.target.value)}>
          {institutes.length === 0 && <option value="">— none —</option>}
          {institutes.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Course Type</div>
        <select data-testid="fee-builder-course-type" className={inputCls} value={courseType} onChange={(e) => setCourseType(e.target.value)} disabled={loadingMasters}>
          {courseTypes.length === 0 ? <option value="">— add types in Masters →</option> : <option value="">Select…</option>}
          {courseTypes.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">Admission Process</div>
        <select data-testid="fee-builder-admission-process" className={inputCls} value={admProc} onChange={(e) => setAdmProc(e.target.value)} disabled={loadingMasters}>
          {admissionProcesses.length === 0 ? <option value="">— add processes in Masters →</option> : <option value="">Select…</option>}
          {admissionProcesses.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </label>
      <label className={`block ${isInstituteLevel ? "opacity-60" : ""}`}>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5 flex items-center justify-between">
          <span>Category (Caste)</span>
          {isInstituteLevel && <span className="text-[9px] normal-case tracking-normal text-[#F26419] font-semibold">N/A</span>}
        </div>
        <select data-testid="fee-builder-category" className={inputCls} value={isInstituteLevel ? "" : category} onChange={(e) => setCategory(e.target.value)} disabled={loadingMasters || isInstituteLevel}>
          {categories.length === 0 ? <option value="">— add categories in Masters →</option> : <option value="">{isInstituteLevel ? "— not applicable —" : "Select…"}</option>}
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </label>
    </div>
  );
}

export default function FeeStructures() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor"].includes(role);

  const [institutes, setInstitutes] = useState([]);
  const [feeHeaders, setFeeHeaders] = useState([]);
  const [feeCategories, setFeeCategories] = useState([]);
  const [feeCourseTypes, setFeeCourseTypes] = useState([]);
  const [feeAdmissionProcesses, setFeeAdmissionProcesses] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(true);

  const [ay, setAY] = useState(DEFAULT_AY);
  const [instId, setInstId] = useState("");
  const [courseType, setCourseType] = useState("");
  const [admProc, setAdmProc] = useState("");
  const [category, setCategory] = useState("");

  const isInstituteLevel = /institute\s*level/i.test(admProc || "");

  const [amounts, setAmounts] = useState({});   // { header_name: string }
  const [dirty, setDirty] = useState(false);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load masters + institutes once
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingMasters(true);
      try {
        const [instRes, headRes, catRes, ctRes, apRes] = await Promise.all([
          api.get("/institutes"),
          api.get("/masters/fee_header"),
          api.get("/masters/fee_category"),
          api.get("/masters/fee_course_type"),
          api.get("/masters/fee_admission_process"),
        ]);
        if (!alive) return;
        const insts = instRes.data || [];
        setInstitutes(insts);
        const defaultInst = insts.find((i) => /autonomous/i.test(i.name || "")) || insts[0];
        setInstId(defaultInst ? defaultInst.id : "");
        setFeeHeaders(headRes.data || []);
        setFeeCategories(catRes.data || []);
        setFeeCourseTypes(ctRes.data || []);
        setFeeAdmissionProcesses(apRes.data || []);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load masters");
      } finally { if (alive) setLoadingMasters(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Load existing fee structure whenever the key changes.
  useEffect(() => {
    if (!instId || !ay || !courseType || !admProc) {
      setAmounts({});
      setDirty(false);
      return;
    }
    const effCategory = isInstituteLevel ? "" : category;
    if (!isInstituteLevel && !effCategory) {
      setAmounts({});
      setDirty(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoadingStructure(true);
      try {
        const { data } = await api.get("/admin/fee-structures", {
          params: {
            institute_id: instId, academic_year: ay,
            course_type: courseType, admission_process: admProc,
            category: effCategory,
          },
        });
        if (!alive) return;
        const map = {};
        (data?.items || []).forEach((it) => { map[it.fee_header] = String(it.amount ?? 0); });
        setAmounts(map);
        setDirty(false);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load fee structure");
      } finally { if (alive) setLoadingStructure(false); }
    })();
    return () => { alive = false; };
  }, [instId, ay, courseType, admProc, category, isInstituteLevel]);

  const filtersComplete = !!(instId && ay && courseType && admProc && (isInstituteLevel || category));

  const grandTotal = useMemo(() => {
    return feeHeaders.reduce((sum, h) => sum + parseAmount(amounts[h.name]), 0);
  }, [amounts, feeHeaders]);

  const setAmount = (name, val) => {
    setAmounts((a) => ({ ...a, [name]: val }));
    setDirty(true);
  };

  const blurNormalise = (name) => {
    const n = parseAmount(amounts[name]);
    setAmounts((a) => ({ ...a, [name]: n ? n.toFixed(2) : "" }));
  };

  const save = async () => {
    if (!filtersComplete) return toast.error("Complete all required filters first");
    const items = feeHeaders
      .map((h) => ({ fee_header: h.name, amount: parseAmount(amounts[h.name]) }))
      .filter((it) => it.amount > 0);
    setSaving(true);
    try {
      await api.put("/admin/fee-structures", {
        institute_id: instId, academic_year: ay,
        course_type: courseType, admission_process: admProc,
        category: isInstituteLevel ? "" : category,
        items,
      });
      toast.success("Fee structure saved");
      setDirty(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const clearAll = () => {
    if (!window.confirm("Clear all amounts in this table? (Not saved until you press Save.)")) return;
    setAmounts({});
    setDirty(true);
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto space-y-6" data-testid="admin-fee-structures">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Institute Admin CMS</div>
        <h1 className="mt-1 font-serif text-3xl md:text-4xl text-[#1c1a17] flex items-center gap-3">
          <Wallet size={26} strokeWidth={1.6} className="text-[#234333]" /> Fee Structure Builder
        </h1>
        <p className="mt-2 text-sm text-[#6b655b] max-w-3xl">
          Build the fee table for the selected combination of <span className="font-semibold">Academic Year × Institute × Course Type × Admission Process × Category</span>. When <em>Admission Process</em> is <span className="font-semibold">Institute Level</span>, Category is not applicable. Enter amounts against each fee head — the Grand Total is calculated automatically.
        </p>
      </div>

      <FiltersCard
        ay={ay} setAY={setAY}
        institutes={institutes} instId={instId} setInstId={setInstId}
        courseTypes={feeCourseTypes} courseType={courseType} setCourseType={setCourseType}
        admissionProcesses={feeAdmissionProcesses} admProc={admProc} setAdmProc={setAdmProc}
        categories={feeCategories} category={category} setCategory={setCategory}
        isInstituteLevel={isInstituteLevel}
        loadingMasters={loadingMasters}
      />

      {!filtersComplete ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-10 text-center" data-testid="fee-builder-empty-hint">
          <Info size={28} strokeWidth={1.5} className="mx-auto text-[#bcb6aa]" />
          <div className="mt-3 text-sm text-[#6b655b]">Select all five filters above to load or start a fee table.</div>
        </div>
      ) : feeHeaders.length === 0 ? (
        <div className="bg-white border border-[#e5e0d8] rounded-sm p-10 text-center">
          <div className="text-sm text-[#6b655b]">No fee headers configured yet. Add entries under <span className="font-semibold">Masters → Show Fees → Fees Header</span>.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden" data-testid="fee-builder-table-card">
          <div className="px-5 py-3.5 bg-[#faf7f0] border-b border-[#e5e0d8]" data-testid="fee-builder-table-header">
            <div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Fee Table</div>
              <div className="text-[13px] text-[#1c1a17] font-semibold mt-0.5 truncate">
                {institutes.find((i) => i.id === instId)?.name || "—"} · {ay} · {courseType} · {admProc}
                {isInstituteLevel ? " · (Category N/A)" : ` · ${category}`}
              </div>
            </div>
            {canEdit && (
              <div className="mt-3 pt-3 border-t border-[#e5e0d8] flex flex-wrap items-center justify-end gap-2" data-testid="fee-builder-actions">
                <button type="button" onClick={clearAll} data-testid="fee-builder-clear" className="text-xs font-semibold text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-4 py-2 rounded-sm border border-[#9e2a2b]/30">
                  Clear Amounts
                </button>
                <button
                  type="button" onClick={save} disabled={saving || loadingStructure}
                  data-testid="fee-builder-save"
                  className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-sm shadow-sm"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {saving ? "Saving…" : (dirty ? "Save Changes" : "Save Fee Structure")}
                </button>
              </div>
            )}
          </div>

          {loadingStructure ? (
            <div className="p-10 text-center text-sm text-[#6b655b] flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading structure…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fee-builder-table">
                <thead className="bg-white border-b border-[#e5e0d8] text-[10px] tracking-[0.2em] uppercase text-[#6b655b]">
                  <tr>
                    <th className="w-16 text-left px-5 py-3 font-semibold">#</th>
                    <th className="text-left px-5 py-3 font-semibold">Fee Head</th>
                    <th className="w-60 text-right px-5 py-3 font-semibold">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e0d8]">
                  {feeHeaders.map((h, i) => {
                    const raw = amounts[h.name] ?? "";
                    return (
                      <tr key={h.id} data-testid={`fee-builder-row-${h.id}`} className="hover:bg-[#f9f8f6]/60">
                        <td className="px-5 py-2.5 text-[#8a8478] text-xs font-mono">{String(i + 1).padStart(2, "0")}</td>
                        <td className="px-5 py-2.5 text-[#1c1a17]">{h.name}</td>
                        <td className="px-5 py-2 text-right">
                          <div className="relative inline-flex items-center">
                            <IndianRupee size={13} className="absolute left-3 text-[#8a8478]" />
                            <input
                              type="text"
                              inputMode="decimal"
                              value={raw}
                              placeholder="0.00"
                              onChange={(e) => setAmount(h.name, e.target.value)}
                              onBlur={() => blurNormalise(h.name)}
                              data-testid={`fee-builder-amount-${h.id}`}
                              disabled={!canEdit}
                              className="w-52 pr-3 pl-7 py-2 text-right border border-[#e5e0d8] rounded-sm text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333] tabular-nums"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#234333] text-white" data-testid="fee-builder-total-row">
                    <td className="px-5 py-3" />
                    <td className="px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-semibold">Grand Total</td>
                    <td className="px-5 py-3 text-right font-mono text-base font-bold tabular-nums" data-testid="fee-builder-grand-total">
                      ₹ {formatINR(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
